'use client';

import { type CSSProperties, useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import { ChevronLeft, ChevronRight, Pause, Play, X } from 'lucide-react';

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@kit/ui/dialog';
import { cn } from '@kit/ui/utils';

import type { ServicesBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { AtmosphericParallax } from './atmospheric-blocks';
import { type LandingRenderContext, landingImageUrl, pickLocalized } from './landing-context';
import styles from './services-ring.module.css';

function useRingRotation(count: number, paused: boolean) {
  const stageRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLUListElement>(null);
  const controls = useRef({ step: (_direction: number) => {}, pause: (_paused: boolean) => {} });

  useEffect(() => {
    const stage = stageRef.current;
    const ring = ringRef.current;
    const window = stage?.ownerDocument.defaultView;
    if (!stage || !ring || !count || !window?.matchMedia || !window.IntersectionObserver) return;
    const document = stage.ownerDocument;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoSpeed = 360 / 70000;
    let angle = 0;
    let direction = 1;
    let velocity = autoSpeed;
    let pending = 0;
    let frame = 0;
    let last = 0;
    let visible = false;
    let stopped = false;
    let hovered = false;
    let focused = false;
    let suppressClick = false;
    let drag: { pointerId: number; startX: number; startAngle: number; lastX: number; lastT: number; moved: boolean } | null = null;
    const paint = () => { ring.style.transform = `rotateY(${angle}deg)`; };
    const canRun = () => visible && !document.hidden && !drag && (pending !== 0 || (!media.matches && !stopped && !hovered && !focused && count > 1));
    const tick = (now: number) => {
      frame = 0;
      if (!canRun()) return;
      const dt = Math.min(now - last, 64);
      last = now;
      const target = stopped || hovered || focused || media.matches ? 0 : autoSpeed * direction;
      velocity += (target - velocity) * Math.min(1, dt * 0.004);
      angle += velocity * dt;
      const step = pending * Math.min(1, dt * 0.008);
      pending -= step;
      angle += step;
      if (Math.abs(pending) < 0.01) { angle += pending; pending = 0; }
      paint();
      if (canRun()) frame = window.requestAnimationFrame(tick);
    };
    const sync = () => {
      if (!canRun()) { window.cancelAnimationFrame(frame); frame = 0; return; }
      if (!frame) { last = window.performance.now(); frame = window.requestAnimationFrame(tick); }
    };
    controls.current = {
      step: (next) => {
        direction = next;
        if (media.matches) { angle += next * 360 / count; paint(); }
        else pending += next * 360 / count;
        sync();
      },
      pause: (value) => { stopped = value; velocity = 0; if (value) pending = 0; sync(); },
    };
    const down = (event: PointerEvent) => {
      if (event.button !== 0 || !event.isPrimary) return;
      suppressClick = false;
      pending = 0;
      drag = { pointerId: event.pointerId, startX: event.clientX, startAngle: angle, lastX: event.clientX, lastT: event.timeStamp, moved: false };
      sync();
    };
    const move = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) > 5) {
        drag.moved = true;
        stage.setPointerCapture(event.pointerId);
        stage.dataset.dragging = 'true';
      }
      if (!drag.moved) return;
      velocity = (event.clientX - drag.lastX) * 0.2 / Math.max(event.timeStamp - drag.lastT, 1);
      drag.lastX = event.clientX;
      drag.lastT = event.timeStamp;
      angle = drag.startAngle + dx * 0.2;
      paint();
    };
    const up = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      suppressClick = drag.moved;
      if (event.type !== 'pointerup' || event.timeStamp - drag.lastT > 80 || media.matches) velocity = 0;
      if (Math.abs(event.clientX - drag.startX) > 30) direction = event.clientX > drag.startX ? 1 : -1;
      drag = null;
      delete stage.dataset.dragging;
      if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      sync();
    };
    const click = (event: MouseEvent) => {
      if (suppressClick && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
    };
    const enter = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      hovered = event.target instanceof window.Element && Boolean(event.target.closest('[data-ring-index]'));
      sync();
    };
    const out = (event: PointerEvent) => {
      hovered = event.relatedTarget instanceof window.Element && Boolean(event.relatedTarget.closest('[data-ring-index]'));
      sync();
    };
    const leave = () => { hovered = false; sync(); };
    const focus = (event: FocusEvent) => {
      focused = true;
      if (event.target instanceof window.HTMLElement && event.target.matches(':focus-visible')) {
        const index = Number(event.target.dataset.ringIndex);
        if (Number.isFinite(index)) { angle = -index * 360 / count; pending = 0; velocity = 0; paint(); }
      }
      sync();
    };
    const blur = (event: FocusEvent) => { focused = event.relatedTarget instanceof window.Node && stage.contains(event.relatedTarget); sync(); };
    const preventDrag = (event: DragEvent) => event.preventDefault();
    const observer = new window.IntersectionObserver(([entry]) => { visible = Boolean(entry?.isIntersecting); sync(); });
    observer.observe(stage);
    stage.addEventListener('pointerdown', down);
    stage.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    stage.addEventListener('lostpointercapture', up);
    stage.addEventListener('click', click, true);
    stage.addEventListener('pointerover', enter);
    stage.addEventListener('pointerout', out);
    stage.addEventListener('pointerleave', leave);
    stage.addEventListener('focusin', focus);
    stage.addEventListener('focusout', blur);
    stage.addEventListener('dragstart', preventDrag);
    document.addEventListener('visibilitychange', sync);
    media.addEventListener('change', sync);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      stage.removeEventListener('pointerdown', down);
      stage.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      stage.removeEventListener('lostpointercapture', up);
      stage.removeEventListener('click', click, true);
      stage.removeEventListener('pointerover', enter);
      stage.removeEventListener('pointerout', out);
      stage.removeEventListener('pointerleave', leave);
      stage.removeEventListener('focusin', focus);
      stage.removeEventListener('focusout', blur);
      stage.removeEventListener('dragstart', preventDrag);
      document.removeEventListener('visibilitychange', sync);
      media.removeEventListener('change', sync);
    };
  }, [count]);

  useEffect(() => { controls.current.pause(paused); }, [paused, count]);
  return { stageRef, ringRef, step: (direction: number) => controls.current.step(direction) };
}

function RingWatermark({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    const view = element?.ownerDocument.defaultView;
    if (!element || !view?.matchMedia || !view.IntersectionObserver) return;
    const document = element.ownerDocument;
    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!visible || document.hidden || media.matches) return;
      const rect = element.getBoundingClientRect();
      element.style.transform = `translate3d(${(rect.top + rect.height / 2 - view.innerHeight / 2) * 0.6}px, 0, 0)`;
    };
    const schedule = () => {
      if (!visible || document.hidden || media.matches) {
        view.cancelAnimationFrame(frame);
        frame = 0;
        if (media.matches) element.style.transform = 'none';
      } else if (!frame) frame = view.requestAnimationFrame(update);
    };
    const observer = new view.IntersectionObserver(([entry]) => { visible = Boolean(entry?.isIntersecting); schedule(); });
    observer.observe(element);
    view.addEventListener('scroll', schedule, { passive: true });
    view.addEventListener('resize', schedule);
    document.addEventListener('visibilitychange', schedule);
    media.addEventListener('change', schedule);
    return () => {
      view.cancelAnimationFrame(frame);
      observer.disconnect();
      view.removeEventListener('scroll', schedule);
      view.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      media.removeEventListener('change', schedule);
    };
  }, []);
  return <div ref={ref} className={styles.watermark} aria-hidden="true">{text}</div>;
}

export function ServicesRing({ block, ctx }: { block: ServicesBlock; ctx: LandingRenderContext }) {
  const [view, setView] = useState({ selected: null as number | null, paused: false, direction: 1, modalContainer: null as HTMLElement | null, modalStyle: {} as CSSProperties });
  const opener = useRef<HTMLButtonElement | null>(null);
  const cards: NonNullable<ServicesBlock['cards']> = block.mode === 'manual' ? block.cards ?? [] : ctx.services.map((service) => ({ title: service.name, description: service.description, href: (block.autoLinkToBooking ?? true) ? '/book' : undefined }));
  const { stageRef, ringRef, step } = useRingRotation(cards.length, view.paused || view.selected !== null);
  const arabic = ctx.locale.startsWith('ar');
  const local = (text?: string, translation?: string) => pickLocalized(text, translation, ctx.locale);
  const selected = view.selected === null ? undefined : cards[view.selected];
  const turn = (direction: number) => { step(direction); setView((previous) => ({ ...previous, direction })); };
  if (!cards.length) return null;

  return (
    <section className={styles.section} data-test="services-ring" dir={arabic ? 'rtl' : undefined}>
      {block.design?.watermark ? <RingWatermark text={block.design.watermark} /> : null}
      <AtmosphericParallax speed={-0.12} className={styles.heading}>
        {block.eyebrow ? <p className={styles.eyebrow}>{local(block.eyebrow, block.eyebrow_ar)}</p> : null}
        {block.title ? <h2>{local(block.title, block.title_ar)}</h2> : null}
        {block.subtitle ? <p className="landing-prose">{local(block.subtitle, block.subtitle_ar)}</p> : null}
      </AtmosphericParallax>
      <div className={styles.carousel}>
        <div className={styles.stage} ref={stageRef} data-ring-stage onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          turn(event.key === 'ArrowLeft' ? -1 : 1);
        }}>
          <div className={styles.direction}>
            <ul className={styles.ring} ref={ringRef} data-ring-track style={{ '--_count': cards.length } as CSSProperties}>
              {cards.map((card, index) => (
                <li key={index} className={styles.item} data-no-image={!card.imageKey || undefined} style={{ '--_index': index, '--_image': card.imageKey ? `url(${JSON.stringify(landingImageUrl(ctx, card.imageKey))})` : 'none' } as CSSProperties}>
                  <button type="button" className={styles.card} data-ring-index={index} draggable={false} aria-label={`${arabic ? 'عرض' : 'View'} ${local(card.title, card.title_ar)}`} aria-haspopup="dialog" onClick={(event) => {
                    opener.current = event.currentTarget;
                    const document = event.currentTarget.ownerDocument;
                    const computed = document.defaultView?.getComputedStyle(event.currentTarget);
                    const modalStyle = computed ? {
                      ...Object.fromEntries(['--background', '--foreground', '--primary', '--primary-foreground', '--border', '--muted', '--muted-foreground', '--ring', '--landing-heading-font', '--landing-canvas', '--landing-ink'].map((name) => [name, computed.getPropertyValue(name)])),
                      fontFamily: computed.fontFamily,
                    } : {};
                    setView((previous) => ({ ...previous, selected: index, modalContainer: document.body, modalStyle }));
                  }}>
                    <span className={styles.photo} aria-hidden="true" />
                    {card.eyebrow ? <span className={styles.tag}>{local(card.eyebrow, card.eyebrow_ar)}</span> : null}
                    <span className={styles.caption}>
                      {card.description ? <small>{local(card.description, card.description_ar)}</small> : null}
                      <strong>{local(card.title, card.title_ar)}</strong>
                    </span>
                  </button>
                </li>
              ))}
              <li className={styles.ground} aria-hidden="true" />
            </ul>
          </div>
        </div>
        {cards.length > 1 ? <div className={styles.controls} dir="ltr">
          <button className={styles.control} type="button" aria-label={arabic ? 'السابق' : 'Previous'} aria-pressed={view.direction === -1} onClick={() => turn(-1)}><ChevronLeft size={20} aria-hidden="true" /></button>
          <button className={cn(styles.control, block.showPlaybackControl === false && styles.focusPlayback)} type="button" aria-label={view.paused ? (arabic ? 'استئناف الدوران' : 'Resume rotation') : (arabic ? 'إيقاف الدوران' : 'Pause rotation')} aria-pressed={view.paused} onClick={() => setView((previous) => ({ ...previous, paused: !previous.paused }))}>{view.paused ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}</button>
          <button className={styles.control} type="button" aria-label={arabic ? 'التالي' : 'Next'} aria-pressed={view.direction === 1} onClick={() => turn(1)}><ChevronRight size={20} aria-hidden="true" /></button>
        </div> : null}
      </div>
      <Dialog open={Boolean(selected)} onOpenChange={(open) => { if (!open) setView((previous) => ({ ...previous, selected: null })); }}>
        <DialogContent className={styles.modal} hideCloseButton portalContainer={view.modalContainer} style={view.modalStyle} dir={arabic ? 'rtl' : 'ltr'} onCloseAutoFocus={(event) => { event.preventDefault(); opener.current?.focus(); }}>
          {selected ? <>
            {selected.imageKey ? <Image className={styles.modalImage} src={landingImageUrl(ctx, selected.imageKey)} alt={local(selected.title, selected.title_ar) ?? ''} width={896} height={640} unoptimized /> : null}
            <div className={styles.modalCaption} data-ring-caption>
              <div className={styles.modalCopy}>
                <DialogDescription asChild><small>{local(selected.description, selected.description_ar) || local(selected.eyebrow, selected.eyebrow_ar)}</small></DialogDescription>
                <DialogTitle asChild><strong>{local(selected.title, selected.title_ar)}</strong></DialogTitle>
              </div>
              {selected.ctaDisabled ? <button className={styles.soon} type="button" disabled>{local(selected.ctaLabel, selected.ctaLabel_ar) || (arabic ? 'قريباً' : 'Coming soon')}</button> : selected.href ? (/^https?:\/\//i.test(selected.href) ? <a className={styles.link} href={selected.href} target="_blank" rel="noopener noreferrer">{local(selected.ctaLabel, selected.ctaLabel_ar) || (arabic ? 'اعرف المزيد' : 'Learn more')}</a> : <Link className={styles.link} href={selected.href}>{local(selected.ctaLabel, selected.ctaLabel_ar) || (arabic ? 'اعرف المزيد' : 'Learn more')}</Link>) : null}
            </div>
            {selected.bullets?.length ? <ul className={styles.bullets}>{(arabic && selected.bullets_ar?.length ? selected.bullets_ar : selected.bullets).map((bullet, index) => <li key={index}>{bullet}</li>)}</ul> : null}
            <DialogClose className={styles.close} aria-label={arabic ? 'إغلاق' : 'Close'}><X size={20} aria-hidden="true" /></DialogClose>
          </> : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
